import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthLink, AuthShell, Divider, GoogleButton } from "@/pages/auth/AuthShell";

const FIELDS = [
  { key: "display_name", label: "Display name", type: "text", auto: "nickname", testid: "register-name-input" },
  { key: "username", label: "Username", type: "text", auto: "username", testid: "register-username-input", hint: "3–30 chars: lowercase letters, numbers, _ or -" },
  { key: "email", label: "Email", type: "email", auto: "email", testid: "register-email-input" },
  { key: "password", label: "Password", type: "password", auto: "new-password", testid: "register-password-input", hint: "At least 8 characters" },
  { key: "confirm", label: "Confirm password", type: "password", auto: "new-password", testid: "register-password-confirm-input" },
];

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: "", username: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    setError("");
    try {
      const { confirm, ...payload } = form;
      await register({ ...payload, username: payload.username.toLowerCase() });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell eyebrow="Join the community" title="Create your creator account"
      footer={<>Already a member? <AuthLink to="/login" testid="register-login-link">Sign in</AuthLink></>}>
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="field-label" htmlFor={`reg-${f.key}`}>{f.label}</label>
            <input id={`reg-${f.key}`} type={f.type} required autoComplete={f.auto} className="field" value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={f.testid} />
            {f.hint && <p className="mt-1 text-xs text-slate-500">{f.hint}</p>}
          </div>
        ))}
        {error && <p role="alert" className="rounded-md border border-red-800/60 bg-red-950/40 px-3 py-2 text-sm text-red-300" data-testid="register-error">{error}</p>}
        <button type="submit" className="btn-gold w-full" disabled={busy} data-testid="register-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}Create account
        </button>
      </form>
    </AuthShell>
  );
}
